/**
 * Delivery Notifications Modal
 *
 * For broadcaster orgs: shows pending spot deliveries from studios.
 * Follows the ReviewResultsModal pattern:
 * - Real-time listener on deliveryNotifications collection
 * - Auto-opens when there are pending deliveries
 * - Acknowledge / Acknowledge All / Remind Me Later
 * - Notification bell integration
 */

import React, { useState, useEffect } from 'react';
import {
  Modal,
  ModalHeader,
  ModalBody,
  ModalFooter,
  Button,
  Spinner,
  Badge,
} from 'reactstrap';
import moment from 'moment';
import { doc, getDoc } from 'firebase/firestore';
import { useOrganizationStore } from '../../store/organization.store';
import { useNotificationsStore } from '../../store/notifications.store';
import { filterByEnabledSince } from '../../services/notificationFilter';
import { getCampaignName } from '../../utils/campaign';
import WavePlayer from '../WavePlayer';

interface DeliveryNotification {
  id: string;
  sourceOrgId: string;
  sourceOrgName: string;
  jobId: string;
  deliveryId: string;
  deliveryCode: string;
  campaign: string;
  client: string;
  period: string;
  stationCount: number;
  trackCount: number;
  deliveredAt: number;
  deliveredByName: string;
  status: string;
  isRapidUpload?: boolean;
  // Enriched from source delivery doc on open
  ftpStatus?: 'sent' | 'partial' | 'queued' | 'failed';
  ftpFileCount?: number;
}

interface DeliveryNotificationsModalProps {
  firebase: any;
}

const DeliveryNotificationsModal: React.FC<DeliveryNotificationsModalProps> = ({ firebase }) => {
  const [notifications, setNotifications] = useState<DeliveryNotification[]>([]);
  const [loading, setLoading] = useState(false);
  const [acknowledgingIds, setAcknowledgingIds] = useState<Set<string>>(new Set());
  const [acknowledgingAll, setAcknowledgingAll] = useState(false);
  const [hasAutoOpened, setHasAutoOpened] = useState(false);
  const [expandedId, setExpandedId] = useState<string | null>(null);
  const [expandedStations, setExpandedStations] = useState<any[]>([]);
  const [loadingDetails, setLoadingDetails] = useState(false);
  const [activeTrackIdx, setActiveTrackIdx] = useState<number | null>(null);

  const { currentOrgId } = useOrganizationStore();

  const isOpen = useNotificationsStore(state => state.showDeliveryNotificationsModal);
  const setIsOpen = useNotificationsStore(state => state.setShowDeliveryNotificationsModal);
  const setPendingDeliveriesCount = useNotificationsStore(state => state.setPendingDeliveriesCount);

  // Reset auto-open flag when org changes
  useEffect(() => {
    setHasAutoOpened(false);
  }, [currentOrgId]);

  // Real-time listener for delivery notifications
  useEffect(() => {
    if (!currentOrgId || !firebase?.subscribeDeliveryNotificationsForOrg) {
      setNotifications([]);
      setPendingDeliveriesCount(0);
      return;
    }

    setLoading(true);
    const unsubscribe = firebase.subscribeDeliveryNotificationsForOrg(
      currentOrgId,
      async (fetched: DeliveryNotification[]) => {
        // Enrich each notification with ftpStatus from the source delivery doc
        const enriched = await Promise.all(fetched.map(async (n) => {
          if (!n.sourceOrgId || !n.jobId || !n.deliveryId) return n;
          try {
            const srcRef = doc(firebase.db, `organizations/${n.sourceOrgId}/jobs/${n.jobId}/deliveries/${n.deliveryId}`);
            const srcSnap = await getDoc(srcRef);
            if (srcSnap.exists()) {
              const d = srcSnap.data() as any;
              return { ...n, ftpStatus: d.ftpStatus, ftpFileCount: d.ftpFileCount };
            }
          } catch (err) {
            console.error('[DeliveryNotificationsModal] ftp lookup failed:', err);
          }
          return n;
        }));
        // Filter out any notifications created before the user last
        // flipped this pref back on — prevents a flood of accumulated
        // backlog when re-enabling. See notificationFilter.ts.
        const enabledSince = useNotificationsStore.getState().inAppEnabledSince;
        const filtered = filterByEnabledSince('deliveryNotifications', enriched, enabledSince, (n) => n.deliveredAt);
        setNotifications(filtered);
        setPendingDeliveriesCount(filtered.length);
        setLoading(false);

        // Auto-open on first load if there are notifications
        if (!hasAutoOpened && filtered.length > 0) {
          setIsOpen(true);
          setHasAutoOpened(true);
        }
      }
    );

    return () => {
      if (unsubscribe) unsubscribe();
    };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentOrgId, firebase]);

  // Close modal when all items are processed
  useEffect(() => {
    if (isOpen && notifications.length === 0 && !loading) {
      setIsOpen(false);
    }
  }, [notifications.length, isOpen, loading, setIsOpen]);

  const handleAccept = async (notif: DeliveryNotification) => {
    if (!currentOrgId || !firebase) return;

    setAcknowledgingIds(prev => new Set(prev).add(notif.id));
    try {
      await firebase.acceptDelivery(
        currentOrgId,
        notif.id,
        notif.sourceOrgId,
        notif.jobId,
        notif.deliveryId,
        notif.deliveryCode,
      );

      // Summarize what happened in a persistent-enough banner for the broadcaster
      const items: string[] = [
        `Added to your library (${notif.trackCount} track${notif.trackCount !== 1 ? 's' : ''})`,
      ];
      if (notif.ftpStatus === 'sent' || notif.ftpStatus === 'partial') {
        items.push(`Pushed to your FTP (${notif.ftpFileCount || 0} file${notif.ftpFileCount === 1 ? '' : 's'})`);
      } else if (notif.ftpStatus === 'queued') {
        items.push('FTP push queued — will send once connection is approved');
      } else if (notif.ftpStatus === 'failed') {
        items.push('FTP push failed — check FTP Settings');
      }
      items.push(`Campaign "${getCampaignName(notif.campaign)}" added to Campaigns`);
      useNotificationsStore.getState().showActionBanner({
        variant: notif.ftpStatus === 'failed' ? 'warning' : 'success',
        title: 'Delivery in your library',
        items,
        autoDismissMs: 10000,
      });
    } catch (error) {
      console.error('Error accepting delivery:', error);
    } finally {
      setAcknowledgingIds(prev => {
        const newSet = new Set(prev);
        newSet.delete(notif.id);
        return newSet;
      });
    }
  };

  const handleAcceptAll = async () => {
    if (!currentOrgId || !firebase) return;

    setAcknowledgingAll(true);
    const snapshot = notifications.slice();
    try {
      await Promise.all(
        snapshot.map(notif =>
          firebase.acceptDelivery(
            currentOrgId,
            notif.id,
            notif.sourceOrgId,
            notif.jobId,
            notif.deliveryId,
            notif.deliveryCode,
          )
        )
      );

      // Aggregate summary
      const totalTracks = snapshot.reduce((sum, n) => sum + (n.trackCount || 0), 0);
      const ftpSent = snapshot.filter(n => n.ftpStatus === 'sent' || n.ftpStatus === 'partial');
      const ftpFileTotal = ftpSent.reduce((sum, n) => sum + (n.ftpFileCount || 0), 0);
      const ftpFailed = snapshot.filter(n => n.ftpStatus === 'failed').length;
      const items: string[] = [
        `Added to your library (${totalTracks} track${totalTracks !== 1 ? 's' : ''})`,
      ];
      if (ftpSent.length > 0) items.push(`Pushed to your FTP (${ftpFileTotal} file${ftpFileTotal === 1 ? '' : 's'})`);
      if (ftpFailed > 0) items.push(`${ftpFailed} FTP push${ftpFailed === 1 ? '' : 'es'} failed — check FTP Settings`);
      items.push(`${snapshot.length} campaign${snapshot.length === 1 ? '' : 's'} added to Campaigns`);
      useNotificationsStore.getState().showActionBanner({
        variant: ftpFailed > 0 ? 'warning' : 'success',
        title: `${snapshot.length} deliver${snapshot.length === 1 ? 'y' : 'ies'} in your library`,
        items,
        autoDismissMs: 10000,
      });
    } catch (error) {
      console.error('Error accepting all deliveries:', error);
    } finally {
      setAcknowledgingAll(false);
    }
  };

  const [rejectingId, setRejectingId] = useState<string | null>(null);
  const [rejectReason, setRejectReason] = useState('');

  const handleReject = async (notif: DeliveryNotification) => {
    if (!currentOrgId || !firebase) return;

    setAcknowledgingIds(prev => new Set(prev).add(notif.id));
    try {
      await firebase.rejectDelivery(
        currentOrgId,
        notif.id,
        notif.sourceOrgId,
        notif.jobId,
        notif.deliveryId,
        notif.deliveryCode,
        rejectReason,
      );
      setRejectingId(null);
      setRejectReason('');
    } catch (error) {
      console.error('Error rejecting delivery:', error);
    } finally {
      setAcknowledgingIds(prev => {
        const newSet = new Set(prev);
        newSet.delete(notif.id);
        return newSet;
      });
    }
  };

  const handleToggleDetails = async (notif: DeliveryNotification) => {
    if (expandedId === notif.id) {
      setExpandedId(null);
      setExpandedStations([]);
      setActiveTrackIdx(null);
      return;
    }

    setExpandedId(notif.id);
    setActiveTrackIdx(null);
    setLoadingDetails(true);

    try {
      const lookupRef = doc(firebase.db, 'deliveryCodes', notif.deliveryCode);
      const lookupDoc = await getDoc(lookupRef);
      if (lookupDoc.exists()) {
        setExpandedStations(lookupDoc.data().stations || []);
      }
    } catch (err) {
      console.error('Error loading delivery details:', err);
    }
    setLoadingDetails(false);
  };

  const getTrackMp3Url = (station: any): string | null => {
    if (!station.mp3Files || station.mp3Files.length === 0) return null;
    if (station.defaultMp3Id) {
      const found = station.mp3Files.find((f: any) => f.storageRef === station.defaultMp3Id);
      if (found) return found.downloadUrl;
    }
    return station.mp3Files[0]?.downloadUrl || null;
  };

  const handleSkip = () => {
    setIsOpen(false);
  };

  if (!isOpen || notifications.length === 0) {
    return null;
  }

  return (
    <Modal
      isOpen={isOpen}
      toggle={handleSkip}
      style={{
        color: '#ffffff',
        maxWidth: expandedId ? '750px' : '600px',
        transition: 'max-width 0.3s ease',
        margin: '1.75rem auto',
      }}
      contentClassName="delete-confirm-modal"
    >
      <ModalHeader
        toggle={handleSkip}
        style={{
          backgroundColor: 'var(--chatbot-color-2)',
          color: '#ffffff',
          borderBottom: '1px solid rgba(255, 255, 255, 0.1)',
        }}
        close={
          <button className="close" onClick={handleSkip} style={{
            color: 'var(--accent-1-color)', opacity: 1, textShadow: 'none',
            background: 'none', border: 'none', fontSize: '1.75rem', fontWeight: 'normal', cursor: 'pointer',
          }}>
            &times;
          </button>
        }
      >
        <span style={{ color: '#fff' }}>
          <i className="tim-icons icon-send mr-2" style={{ color: 'var(--accent-1-color)' }}></i>
          Spot Deliveries ({notifications.length})
        </span>
      </ModalHeader>

      <ModalBody style={{
        backgroundColor: 'var(--chatbot-color-1)',
        color: '#ffffff',
        padding: '1rem',
        maxHeight: '60vh',
        overflowY: 'auto',
      }}>
        {/* Auto-copy explainer — under the new delivery model the audio +
            campaign already landed in the broadcaster's library at the
            moment the studio delivered. This modal is informational;
            Dismiss just clears the notification, Reject undoes the
            auto-copy. */}
        <div style={{
          marginBottom: '0.75rem',
          padding: '0.6rem 0.75rem',
          backgroundColor: 'rgba(80, 200, 120, 0.06)',
          border: '1px solid rgba(80, 200, 120, 0.2)',
          borderRadius: '6px',
          color: 'rgba(255,255,255,0.75)',
          fontSize: '0.8rem',
          lineHeight: 1.4,
        }}>
          These deliveries are already in your Library and Campaigns.
          {' '}
          <strong style={{ color: '#fff' }}>Dismiss</strong> just clears the notification —
          {' '}
          <strong style={{ color: '#ff8080' }}>Reject</strong> removes them from your org.
        </div>
        {loading ? (
          <div className="text-center py-4">
            <Spinner style={{ color: 'var(--accent-1-color)' }} />
          </div>
        ) : (
          <div style={{ display: 'flex', flexDirection: 'column', gap: '0.75rem' }}>
            {notifications.map((notif) => {
              const isAcknowledging = acknowledgingIds.has(notif.id);
              return (
                <div key={notif.id} style={{
                  padding: '1rem',
                  backgroundColor: 'rgba(255,255,255,0.03)',
                  borderRadius: '8px',
                  border: '1px solid rgba(255, 255, 255, 0.08)',
                }}>
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: '0.5rem' }}>
                    <div>
                      <div style={{ color: '#fff', fontWeight: '600', fontSize: '0.95rem' }}>
                        {getCampaignName(notif.campaign)}
                      </div>
                      <div style={{ color: 'rgba(255,255,255,0.5)', fontSize: '0.8rem' }}>
                        From <strong style={{ color: 'var(--accent-1-color)' }}>{notif.sourceOrgName}</strong>
                        {notif.deliveredByName && <> by {notif.deliveredByName}</>}
                      </div>
                    </div>
                    <div style={{ display: 'flex', gap: '0.35rem', alignItems: 'center' }}>
                      {notif.isRapidUpload && (
                        <Badge style={{
                          backgroundColor: 'rgba(255, 180, 50, 0.2)',
                          color: '#ffb432',
                          fontSize: '0.7rem',
                        }}>
                          Rapid Upload
                        </Badge>
                      )}
                      {notif.ftpStatus && (() => {
                        const map: Record<string, { bg: string; color: string; label: string }> = {
                          sent: { bg: 'rgba(90, 170, 255, 0.2)', color: '#5aaaff', label: `FTP${notif.ftpFileCount ? ` · ${notif.ftpFileCount}` : ''}` },
                          partial: { bg: 'rgba(255, 180, 50, 0.2)', color: '#ffb432', label: 'FTP · partial' },
                          queued: { bg: 'rgba(160, 130, 255, 0.2)', color: '#a082ff', label: 'FTP · queued' },
                          failed: { bg: 'rgba(255, 80, 80, 0.2)', color: '#ff5050', label: 'FTP · failed' },
                        };
                        const m = map[notif.ftpStatus];
                        if (!m) return null;
                        return (
                          <Badge style={{ backgroundColor: m.bg, color: m.color, fontSize: '0.7rem' }}>
                            {m.label}
                          </Badge>
                        );
                      })()}
                      <Badge style={{
                        backgroundColor: 'rgba(80, 200, 120, 0.2)',
                        color: '#50c878',
                        fontSize: '0.7rem',
                      }}>
                        New
                      </Badge>
                    </div>
                  </div>

                  <div style={{ display: 'flex', gap: '1rem', marginBottom: '0.75rem', fontSize: '0.8rem' }}>
                    {notif.client && (
                      <span style={{ color: 'rgba(255,255,255,0.6)' }}>
                        Client: <strong style={{ color: '#fff' }}>{notif.client}</strong>
                      </span>
                    )}
                    {notif.period && (
                      <span style={{ color: 'rgba(255,255,255,0.6)' }}>
                        Period: <strong style={{ color: '#fff' }}>{notif.period}</strong>
                      </span>
                    )}
                    <span style={{ color: 'rgba(255,255,255,0.6)' }}>
                      {notif.stationCount} station{notif.stationCount !== 1 ? 's' : ''} &middot; {notif.trackCount} track{notif.trackCount !== 1 ? 's' : ''}
                    </span>
                  </div>

                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                    <span style={{ color: 'rgba(255,255,255,0.3)', fontSize: '0.75rem' }}>
                      {notif.deliveredAt ? moment(notif.deliveredAt).format('DD/MM/YYYY HH:mm') : ''}
                    </span>
                    <div style={{ display: 'flex', gap: '0.5rem' }}>
                      <button
                        onClick={() => handleToggleDetails(notif)}
                        style={{
                          background: 'none', border: '1px solid rgba(255,255,255,0.2)',
                          color: expandedId === notif.id ? 'var(--color-accent)' : 'rgba(255,255,255,0.6)',
                          cursor: 'pointer',
                          fontSize: '0.75rem', padding: '3px 8px', borderRadius: '4px',
                        }}
                      >
                        {expandedId === notif.id ? 'Hide' : 'Preview'}
                      </button>
                      <button
                        onClick={() => { setRejectingId(rejectingId === notif.id ? null : notif.id); setRejectReason(''); }}
                        disabled={isAcknowledging || acknowledgingAll}
                        style={{
                          background: 'none', border: '1px solid rgba(255,80,80,0.3)',
                          color: '#ff5050', cursor: 'pointer',
                          fontSize: '0.75rem', padding: '3px 8px', borderRadius: '4px',
                        }}
                      >
                        Reject
                      </button>
                      <Button
                        size="sm"
                        className="btn-success-custom"
                        onClick={() => handleAccept(notif)}
                        disabled={isAcknowledging || acknowledgingAll}
                        style={{ fontSize: '0.75rem', padding: '3px 10px' }}
                        title="Clear this notification. The campaign and audio are already in your library — Dismiss just acknowledges you've seen it."
                      >
                        <span style={{ position: 'relative', zIndex: 2 }}>
                          {isAcknowledging ? <Spinner size="sm" /> : 'Dismiss'}
                        </span>
                      </Button>
                    </div>

                    {/* Rejection reason form */}
                    {rejectingId === notif.id && (
                      <div style={{
                        marginTop: '0.5rem', padding: '0.5rem',
                        backgroundColor: 'rgba(255, 80, 80, 0.05)',
                        border: '1px solid rgba(255, 80, 80, 0.15)',
                        borderRadius: '6px',
                      }}>
                        <textarea
                          value={rejectReason}
                          onChange={(e) => setRejectReason(e.target.value)}
                          placeholder="Reason for rejecting (optional)..."
                          style={{
                            width: '100%', padding: '6px 8px', fontSize: '0.8rem',
                            backgroundColor: 'rgba(255,255,255,0.08)',
                            border: '1px solid rgba(255,255,255,0.15)',
                            color: '#fff', borderRadius: '4px', resize: 'none',
                            marginBottom: '0.5rem',
                          }}
                          rows={2}
                        />
                        <div style={{ display: 'flex', gap: '0.5rem', justifyContent: 'flex-end' }}>
                          <button
                            onClick={() => { setRejectingId(null); setRejectReason(''); }}
                            style={{
                              background: 'none', border: '1px solid rgba(255,255,255,0.15)',
                              color: 'rgba(255,255,255,0.5)', cursor: 'pointer',
                              fontSize: '0.7rem', padding: '2px 8px', borderRadius: '4px',
                            }}
                          >
                            Cancel
                          </button>
                          <button
                            onClick={() => handleReject(notif)}
                            disabled={isAcknowledging}
                            style={{
                              background: 'none', border: '1px solid rgba(255,80,80,0.4)',
                              color: '#ff5050', cursor: 'pointer',
                              fontSize: '0.7rem', padding: '2px 8px', borderRadius: '4px',
                            }}
                          >
                            Confirm Rejection
                          </button>
                        </div>
                      </div>
                    )}

                    {/* Expandable track preview */}
                    {expandedId === notif.id && (
                      <div style={{ marginTop: '0.75rem', borderTop: '1px solid rgba(255,255,255,0.06)', paddingTop: '0.75rem' }}>
                        {loadingDetails ? (
                          <div className="text-center py-2">
                            <Spinner size="sm" style={{ color: 'var(--color-accent)' }} />
                          </div>
                        ) : expandedStations.length === 0 ? (
                          <div style={{ color: 'rgba(255,255,255,0.4)', fontSize: '0.8rem', textAlign: 'center' }}>No tracks found</div>
                        ) : (
                          <div style={{ display: 'flex', flexDirection: 'column', gap: '0.5rem' }}>
                            {expandedStations.filter((s: any) => s.linkedTrackId || (s.mp3Files && s.mp3Files.length > 0) || (s.wavFiles && s.wavFiles.length > 0)).map((station: any, idx: number) => {
                              const mp3Url = getTrackMp3Url(station);
                              const isActive = activeTrackIdx === idx;
                              return (
                                <div key={idx} style={{
                                  padding: '0.5rem 0.75rem',
                                  backgroundColor: 'rgba(255,255,255,0.02)',
                                  borderRadius: '6px',
                                  border: isActive ? '1px solid rgba(255,255,255,0.15)' : '1px solid rgba(255,255,255,0.04)',
                                }}>
                                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                                    <div>
                                      <span style={{ color: '#fff', fontSize: '0.85rem', fontWeight: 500 }}>
                                        {station.trackName || station.flightingCode || 'Untitled'}
                                      </span>
                                      <span style={{ color: 'rgba(255,255,255,0.4)', fontSize: '0.75rem', marginLeft: '8px' }}>
                                        {station.station}
                                      </span>
                                      {station.language && (
                                        <span style={{ color: 'rgba(255,255,255,0.3)', fontSize: '0.7rem', marginLeft: '8px' }}>
                                          {Array.isArray(station.language)
                                            ? station.language.map((l: any) => typeof l === 'object' ? l.label : l).join(', ')
                                            : station.language}
                                        </span>
                                      )}
                                    </div>
                                    {mp3Url && (
                                      <button
                                        onClick={() => setActiveTrackIdx(isActive ? null : idx)}
                                        style={{
                                          background: 'none', border: '1px solid rgba(255,255,255,0.2)',
                                          color: isActive ? 'var(--color-accent)' : 'rgba(255,255,255,0.5)',
                                          cursor: 'pointer', fontSize: '0.7rem', padding: '2px 8px', borderRadius: '4px',
                                        }}
                                      >
                                        {isActive ? 'Close' : 'Play'}
                                      </button>
                                    )}
                                  </div>
                                  {isActive && mp3Url && (
                                    <div style={{ marginTop: '0.5rem' }}>
                                      <WavePlayer
                                        id={`previewAudioPlayer-delivery-notif-${idx}`}
                                        file={mp3Url}
                                        noAutoplay
                                        isActiveTrack={true}
                                      />
                                    </div>
                                  )}
                                </div>
                              );
                            })}
                          </div>
                        )}
                      </div>
                    )}
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </ModalBody>

      <ModalFooter style={{
        backgroundColor: 'var(--chatbot-color-2)',
        borderTop: '1px solid rgba(255, 255, 255, 0.1)',
        justifyContent: 'space-between',
        padding: '0.75rem 1rem',
      }}>
        <Button
          className="btn-info-custom"
          onClick={handleSkip}
          size="sm"
        >
          <span style={{ position: 'relative', zIndex: 2 }}>Remind Me Later</span>
        </Button>
        <Button
          className="btn-success-custom"
          onClick={handleAcceptAll}
          disabled={acknowledgingAll || notifications.length === 0}
          size="sm"
        >
          <span style={{ position: 'relative', zIndex: 2 }}>
            {acknowledgingAll ? (
              <><Spinner size="sm" className="mr-1" /> Dismissing...</>
            ) : (
              `Dismiss All (${notifications.length})`
            )}
          </span>
        </Button>
      </ModalFooter>
    </Modal>
  );
};

export default DeliveryNotificationsModal;
